import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { formatErr } from "../lib/auth";
import TopNav from "../components/TopNav";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      // Server intentionally returns 200 regardless to avoid leaking account existence.
      setSent(true);
      if (res.data?.dev_reset_link) setDevLink(res.data.dev_reset_link);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      <TopNav landing />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md flat-card p-8 scanlines" data-testid="forgot-card">
          <div className="flex items-center gap-3 mb-2">
            <Shield size={22} color="var(--cyan)" strokeWidth={1.5} />
            <div className="overline">// CREDENTIAL_RECOVERY</div>
          </div>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight mb-1">Reset Credential</h1>
          <p className="text-sm text-[var(--text-2)] mb-6">
            Enter your operator email. If an account exists, Sentinel will dispatch a secure reset link valid for 15 minutes.
          </p>

          {!sent && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Email</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-tac mt-1" placeholder="you@yourcompany.com"
                  data-testid="forgot-email"
                />
              </div>

              {err && (
                <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="forgot-submit">
                {busy ? "Transmitting…" : "Send Reset Link"}
              </button>
            </form>
          )}

          {sent && (
            <div className="space-y-4" data-testid="forgot-sent">
              <div className="flex items-start gap-3 border border-[var(--green)] p-4">
                <Mail size={18} color="var(--green)" strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-display font-bold uppercase text-sm text-[var(--green)]">Transmission Sent</div>
                  <p className="text-sm text-[var(--text-2)] mt-1">
                    If an account is registered to that address, a reset link has been dispatched. Check your inbox (and spam folder).
                  </p>
                </div>
              </div>
              {devLink && (
                <div className="border border-[var(--amber)] p-3" data-testid="forgot-dev-link">
                  <div className="overline" style={{ color: "var(--amber)" }}>// DEV_MODE_LINK</div>
                  <a
                    href={devLink}
                    className="font-mono text-xs text-[var(--cyan)] break-all hover:underline"
                  >
                    {devLink}
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 border-t border-[var(--border)] pt-4">
            <Link to="/login" className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-[var(--text-2)] hover:text-[var(--cyan)]" data-testid="forgot-back-login">
              <ArrowLeft size={12} /> Return to login
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
